import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { useEditorStore } from '../state/editorStore.js';

export type SaveState = 'gespeichert' | 'ungespeichert' | 'speichert' | 'fehler';

export interface Autosave {
  state: SaveState;
  error: string | null;
  /** Sofort speichern (Menü „Speichern“, Strg+S). */
  saveNow(): Promise<void>;
  lastSavedAt: Date | null;
}

export const AUTOSAVE_DELAY_MS = 1500;

const LAST_DOCUMENT_KEY = 'abgen.lastDocument';

export function rememberDocument(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_DOCUMENT_KEY, id);
    else localStorage.removeItem(LAST_DOCUMENT_KEY);
  } catch {
    // Ohne Speicher wird beim nächsten Start die Dokumentliste gezeigt.
  }
}

export function lastDocumentId(): string | null {
  try {
    return localStorage.getItem(LAST_DOCUMENT_KEY);
  } catch {
    return null;
  }
}

/**
 * Autosave (Phase 1.7): speichert kurz nach der letzten Änderung und legt
 * dabei serverseitig eine Revision an.
 */
export function useAutosave(enabled: boolean): Autosave {
  const dirty = useEditorStore((state) => state.dirty);
  const document_ = useEditorStore((state) => state.document);
  const remoteId = useEditorStore((state) => state.remoteId);
  const [state, setState] = useState<SaveState>('gespeichert');
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  // Alle Aufrufer warten auf dieselbe Warteschlange, auch während eines Autosaves.
  const saving = useRef<Promise<void> | null>(null);

  const save = useCallback((): Promise<void> => {
    if (saving.current) return saving.current;

    const flush = async () => {
      while (true) {
        const { dirty, remoteId: id, document: snapshot } = useEditorStore.getState();
        if (!dirty || !id) return;
        setState('speichert');
        try {
          await api.saveDocument(id, snapshot);
        } catch (cause) {
          // Eine Antwort für ein inzwischen geschlossenes Blatt gehört nicht zum neuen Blatt.
          if (useEditorStore.getState().remoteId === id) {
            setState('fehler');
            setError(cause instanceof ApiError ? cause.message : 'Speichern fehlgeschlagen.');
          }
          throw cause;
        }

        const current = useEditorStore.getState();
        if (current.remoteId === id) {
          setError(null);
          setLastSavedAt(new Date());
          if (current.document === snapshot) {
            current.markSaved();
            setState('gespeichert');
            return;
          }
        }
        // Änderungen während der Anfrage sofort nachspeichern. Ein explizites Speichern
        // (etwa vor dem Druck) ist erst fertig, wenn auch diese Änderungen gesichert sind.
      }
    };

    saving.current = flush().finally(() => {
      saving.current = null;
    });
    return saving.current;
  }, []);

  useEffect(() => {
    if (!enabled || !dirty || !remoteId) {
      if (!dirty) setState((current) => (current === 'fehler' ? current : 'gespeichert'));
      return;
    }
    if (!saving.current) setState('ungespeichert');
    const timer = setTimeout(() => {
      // Hintergrundfehler werden im Editor angezeigt; explizite Aufrufer erhalten sie als Rejection.
      void save().catch(() => undefined);
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [dirty, document_, enabled, remoteId, save]);

  return {
    state,
    error,
    saveNow: save,
    lastSavedAt,
  };
}
